import { Bar, BarChart, CartesianGrid, XAxis } from 'recharts';
import { Calendar } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@r/utils/utils.ts';

import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@ui/card.tsx';

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent
} from '@r/components/ui/chart.tsx';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useColorGradient } from '@client/hooks/use-color-gradient.tsx';
import type { InsightUserInsightUserMeReadModel } from '@r/api/generated/client.schemas.ts';

type UserInsights = InsightUserInsightUserMeReadModel;

const ProgressMultiObjectives = ({
  userInsights,
  className
}: {
  userInsights: UserInsights;
  className?: string;
}) => {
  const { t } = useTranslation();

  const rawDataPerMonth = userInsights?.insightPerformance?.targetsAmountPerMonth || [];

  const allTargetReferences = new Set<string>();
  for (const monthData of rawDataPerMonth) {
    for (const target of monthData.targets) {
      allTargetReferences.add(target.targetReference);
    }
  }
  const objectiveKeys: string[] = Array.from(allTargetReferences);

  type ChartMonthData = { month: string } & Record<string, number>;

  const dataToUse: ChartMonthData[] = rawDataPerMonth.map((monthData) => {
    const fullMonthName = format(new Date(monthData.year, monthData.month - 1), 'MMMM', {
      locale: fr
    });

    const chartMonth: ChartMonthData = {
      month: fullMonthName.charAt(0).toUpperCase() + fullMonthName.slice(1)
    } as ChartMonthData;
    for (const key of objectiveKeys) {
      chartMonth[key] = 0;
    }

    for (const target of monthData.targets) {
      if (objectiveKeys.includes(target.targetReference)) {
        chartMonth[target.targetReference] = target.totalAmount;
      }
    }

    return chartMonth;
  });

  const dynamicColors = useColorGradient(objectiveKeys.length);

  const dynamicChartConfig: ChartConfig = {};
  objectiveKeys.forEach((key, index) => {
    dynamicChartConfig[key] = {
      label: key,
      color: dynamicColors[index] || 'hsl(var(--muted))'
    };
  });

  const chartConfig = dynamicChartConfig;

  return (
    <Card className={cn('flex flex-col justify-between w-full border', className)}>
      <CardHeader className="space-y-0">
        <CardTitle className="flex items-center mb-6 space-x-2 text-xl font-author-complete font-regular">
          <div className="flex gap-2 items-center pb-4 font-bold">
            <Calendar />
            {t('performance.target.multi.objective.title')}
          </div>
        </CardTitle>
      </CardHeader>

      <CardContent>
        <ChartContainer config={chartConfig} aspectClassName="aspect-[4/1]">
          <BarChart barSize={20} accessibilityLayer data={dataToUse}>
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="month"
              tickLine={false}
              tickMargin={10}
              axisLine={false}
              tickFormatter={(value) => value.slice(0, 3)}
            />

            <ChartTooltip cursor={false} content={<ChartTooltipContent />} />

            {objectiveKeys.map((key, index) => (
              <Bar
                className="cursor-pointer"
                key={key}
                dataKey={key}
                stackId="a"
                fill={chartConfig[key]?.color || 'hsl(var(--muted))'}
                radius={
                  objectiveKeys.length === 1
                    ? [20, 20, 0, 0]
                    : index === 0
                      ? [0, 0, 20, 20]
                      : index === objectiveKeys.length - 1
                        ? [20, 20, 0, 0]
                        : [0, 0, 0, 0]
                }
              />
            ))}
          </BarChart>
        </ChartContainer>
      </CardContent>

      <CardFooter>
        <p className="m-auto text-sm text-muted-foreground">
          {t('performance.target.multi.objective.footer')}
        </p>
      </CardFooter>
    </Card>
  );
};

export { ProgressMultiObjectives };
